import { useNavigate } from 'react-router-dom'
import { ArrowLeft, X } from 'lucide-react'
import useIsMobile from 'hooks/useIsMobile'
import routes from 'constants/routes'

/**
 * Returns to the previous screen from a mobile page.
 *
 * On a phone the app is a short list of destinations reached from the Home
 * tiles, and until now the only way back was opening the hamburger and picking
 * Home. This is the one-tap exit.
 *
 * **Two shapes, for two different acts.**
 *
 * `back` carries an ARROW, because it goes UP one level: from a location to the
 * list of locations. ArrowLeft rather than ChevronLeft, because the chevron
 * already means "drill in" on the list rows and in the secondary nav.
 *
 * `close` carries an X and goes straight to `fallbackTo`, because on a primary
 * page there is no level above to return to. The page is being left, not
 * ascended out of, and an X is what people reach for to do that.
 *
 * Getting these the wrong way round is not cosmetic: an X on a detail screen
 * promises dismissal it never delivers, and an arrow on a primary page implies
 * a step back that does not exist.
 *
 * Renders nothing on desktop, where the persistent sidebar already makes every
 * destination one click away — so callers can drop it in unconditionally
 * rather than each screen repeating the same guard.
 */
const MobileBackButton = ({
	className = '',
	fallbackTo = routes.HOME,
	mode = 'back',
}: {
	className?: string
	/**
	 * `back` goes up one level. `close` leaves for `fallbackTo` outright, which
	 * is what a primary page needs: there is nothing above it to go up to.
	 */
	mode?: 'back' | 'close'
	/**
	 * Where to land when there is nothing to go back to. Defaults to the
	 * desktop home; the field app passes its own, or the button would throw a
	 * user out of the app they are standing in.
	 */
	fallbackTo?: string
}) => {
	const navigate = useNavigate()
	const isMobile = useIsMobile()

	if (!isMobile) return null

	const handleBack = () => {
		/**
		 * `navigate(-1)` alone is not enough. On a cold load — a bookmark, a
		 * shared link, a fresh tab — there is no history entry to pop and the
		 * button would silently do nothing, which reads as broken. React Router
		 * tracks the position in the history stack on `window.history.state.idx`;
		 * at 0 there is nothing behind us, so fall back to Home.
		 */
		const idx = (window.history.state as { idx?: number } | null)?.idx
		if (typeof idx === 'number' && idx > 0) navigate(-1)
		else navigate(fallbackTo)
	}

	// Closing is deterministic: it always lands in the same place, rather than
	// wherever the history happens to lead. That predictability is the point.
	const handleClose = () => navigate(fallbackTo)

	return (
		<button
			type="button"
			onClick={mode === 'close' ? handleClose : handleBack}
			aria-label={mode === 'close' ? 'Close' : 'Back'}
			/*
				The BUTTON stays 44px square: that is the minimum touch target in the
				MDT mobile guidance, and it keeps this aligned with the 44px chat and
				menu controls it sits beside. Only the blue disc inside it shrinks.
				Shrinking the button itself would have bought the smaller look at the
				cost of a target that is harder to hit one-handed in a car park,
				which is the whole operating condition of the field app.
			*/
			className={`group inline-flex items-center justify-center w-11 h-11 flex-shrink-0 bg-transparent p-0 focus:outline-none ${className}`}
		>
			<span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-transform duration-150 group-active:scale-[0.92] group-focus-visible:ring-2 group-focus-visible:ring-ring">
				{mode === 'close' ? <X className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
			</span>
		</button>
	)
}

export default MobileBackButton
